import { cn } from 'cn';
import { type ReactElement } from 'react';

import { Icon } from '@vestigas/ui/atoms';
import { Tooltip } from '~/ui/atoms';
import { ListItem } from '~/ui/molecules/VirtualizedList';

import { type Item } from '../types';

type ManageableListItemProps = {
  readonly fieldName: string;
  readonly isLocked?: boolean;
  readonly item: Item;
  readonly lockedTooltip?: string;
  readonly onClick: (item: Item) => void;
  readonly type: Item['type'];
  readonly variant: 'selectable' | 'selected';
};

export const ManageableListItem = ({
  fieldName,
  isLocked = false,
  item,
  lockedTooltip,
  onClick,
  type,
  variant,
}: ManageableListItemProps): ReactElement => {
  const getIcon = (): ReactElement | undefined => {
    if (variant === 'selected') {
      if (isLocked) {
        return <Icon metaphor="sync" />;
      }

      switch (type) {
        case 'added': {
          return <Icon metaphor="delete" />;
        }

        case 'current': {
          return <Icon metaphor="delete" />;
        }

        case 'removed': {
          return <Icon metaphor="replay" />;
        }

        default: {
          return undefined;
        }
      }
    }

    return type === 'removed' ? <Icon metaphor="replay" /> : undefined;
  };

  const handleClick = (): void => {
    if (isLocked) {
      return;
    }

    onClick(item);
  };

  const listItem = (
    <ListItem
      className={cn('leading-tight decoration-tiga-text-lighter', {
        'bg-green-200/35!': type === 'added',
        'bg-red-200/35! line-through decoration-gray-500': type === 'removed',
        'cursor-default! text-tiga-text-lighter': isLocked,
      })}
      icon={getIcon()}
      onClick={handleClick}
    >
      <div className="truncate">{item[fieldName]}</div>
      {Object.hasOwn(item, 'secondaryInfo') && (
        <div className="truncate text-sm text-tiga-text-lighter">
          {item.secondaryInfo}
        </div>
      )}
    </ListItem>
  );

  if (isLocked && lockedTooltip) {
    return (
      <Tooltip title={lockedTooltip} shouldWrapChildren>
        {listItem}
      </Tooltip>
    );
  }

  return listItem;
};
